import React from 'react';
import { useProgramContext } from '../../../../../../hooks/useProgramContext';
import CreationFlowDialog from '../../../capability-setup/CreationFlowDialog';
import type { TrainingRecommendation } from '../../../capability-setup/create/trainingSnapshot';
import { useCapabilityRoles } from '../../../capability-setup/useCapabilityRoles';
import { useCapabilitySets } from '../../../capability-setup/useCapabilitySets';
import { sourceOf } from '../../../capability-setup/capabilitySource';
import type { CapabilityRecommendationView } from '../../../../../../api/capability';
import type { RecommendedCourse } from './knowledgeGaps';

/**
 * The Capability release's Publish Training flow, opened over the dashboard:
 *  - from Add New Program, once the admin chose a Course or Guided Course built
 *    with Elliott (`fresh`: the stage it was opened from, if any, and whether
 *    it is a Guided Course);
 *  - from a recommended course, filled in from the recommendation (its name,
 *    stage, audience and purpose; for a capability recommendation, its role and
 *    capability too) for the admin to review; or
 *  - on a program built in the flow, reopened at the step it was left
 *    (Continue on its card); once published, that is Members.
 * Mounted only while it is open: that is when it reads the roles the flow
 * picks capabilities from, and the flow waits for them.
 */
const BuildTrainingDialog: React.FC<{
  recommended?: {
    course: RecommendedCourse;
    /** The stage (pillar) the recommendation sits in on the framework. */
    pillarId: string | null;
    /** A capability recommendation: the Training is for this role and this capability of it. */
    capability?: CapabilityRecommendationView;
  };
  fresh?: { pillarId: string | null; guided: boolean };
  resume?: { groupId: string; published?: boolean };
  onClose: () => void;
  /** The program (by id) was created, or moved on: the dashboard reads its programs again. */
  onProgramChanged: (groupId: string) => void;
  /**
   * Structure has been asked for: the Training is under way. `recommendation`:
   * the capability recommendation it was built from, kept with the program, so
   * it is known on a later visit too (Continue on its card).
   */
  onGenerate: (recommendation: TrainingRecommendation | null) => void;
  /** Invite & Finish: the Training is built and its people invited. */
  onFinished: (groupId: string) => void;
}> = ({ recommended, fresh, resume, onClose, onProgramChanged, onGenerate, onFinished }) => {
  const { orgId, isPlatformAdmin } = useProgramContext();
  const { roles, teamsList, mode } = useCapabilityRoles(orgId, isPlatformAdmin);
  const capabilitySets = useCapabilitySets(orgId, isPlatformAdmin);
  const capability = recommended?.capability;
  // A recommendation on a shared capability builds a Training of its type (Development, Standards).
  const source = sourceOf(capability);
  return (
    <CreationFlowDialog
      kind="training"
      roles={roles}
      sets={capabilitySets.sets}
      rolesLoading={mode === 'loading' || capabilitySets.mode === 'loading'}
      teamsListId={teamsList?.listId ?? null}
      trainingSeed={
        fresh
          ? { name: '', stageId: fresh.pillarId, audienceIds: [], purpose: '', guided: fresh.guided }
          : recommended && {
              name: recommended.course.name,
              stageId: recommended.pillarId,
              audienceIds: recommended.course.audienceValueIds,
              purpose: recommended.course.purpose,
              // For one role only, on the one capability, as a Role Excellence Training.
              ...(capability
                ? {
                    roleIds: [capability.roleId],
                    capabilityIds: [capability.capabilityId],
                    category: source === 'role' ? ('role-excellence' as const) : source,
                    recommendation: { roleId: capability.roleId, capabilityId: capability.capabilityId }
                  }
                : null)
            }
      }
      trainingResume={resume}
      onProgramChanged={onProgramChanged}
      onTrainingGenerate={onGenerate}
      onTrainingFinished={onFinished}
      onClose={onClose}
    />
  );
};

export default BuildTrainingDialog;
